import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import "./AboutUs.css";
export default function AboutUs() {
  const navigate = useNavigate();
  const location = useLocation();

  // Scroll to section based on URL hash
  useEffect(() => {
    const hash = location.hash.replace('#', '');
    if (hash) {
      setTimeout(() => {
        const el = document.getElementById(hash);
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  }, [location.hash]);

  const [showPolicyModal, setShowPolicyModal] = useState(false);
  const [selectedPolicy, setSelectedPolicy] = useState(null);
  const [showPricing, setShowPricing] = useState(false);

  const policies = {
    privacy: {
  title: "Privacy Policy",
  content: `
PRIVACY POLICY

Last Updated: January 2026

1. INTRODUCTION
Vagmee ("Company", "we", "our", or "us") values your privacy and is committed to protecting your personal information. This Privacy Policy explains how we collect, use, store, and protect information when you access or use our platform, website, and services (collectively, the "Service").

By using Vagmee, you agree to the practices described in this Privacy Policy.

---

2. INFORMATION WE COLLECT
We collect the following categories of information:

a) Information You Provide Directly
- Name, email address, and mobile number
- Account and profile information
- Interview responses (audio, text, or video where applicable)
- Feedback and communications you share with us

b) Automatically Collected Information
- Device and browser information
- Usage data such as pages visited, time spent, and feature interactions
- IP address and approximate location
- Cookies and similar technologies for analytics and performance

c) Interview & Performance Data
- Transcripts generated from speech-to-text
- AI-generated interview analysis, scores, and reports
- Historical interview records linked to your account

---

3. HOW WE USE YOUR INFORMATION
We use your information to:
- Provide, operate, and maintain the Service
- Conduct AI-powered interview simulations and generate reports
- Improve product performance, accuracy, and reliability
- Communicate service updates, support messages, and important notices
- Monitor usage patterns and prevent misuse or fraud
- Comply with legal and regulatory requirements

We do NOT sell your personal data to third parties.

---

4. AI & AUTOMATED PROCESSING DISCLOSURE
Vagmee uses artificial intelligence and automated systems to:
- Transcribe interview responses
- Analyze communication, clarity, and role relevance
- Generate interview feedback and reports

AI-generated outputs are provided for informational and practice purposes only and should not be considered professional hiring, legal, or career advice.

---

5. DATA SHARING & THIRD PARTIES
We may share limited data with trusted third-party service providers strictly for:
- Authentication
- Payment processing
- Analytics
- Cloud infrastructure
- Customer support

All such providers are required to follow reasonable data protection practices.

---

6. DATA SECURITY
We implement commercially reasonable and industry-standard security measures to protect your information from unauthorized access, loss, misuse, alteration, or disclosure.

However, no system is completely secure, and we cannot guarantee absolute security.

---

7. DATA RETENTION
We retain your data only for as long as necessary to:
- Provide our services
- Fulfill legal or regulatory obligations
- Resolve disputes
- Improve product quality

You may request deletion of your data at any time, subject to applicable laws.

---

8. YOUR RIGHTS
You have the right to:
- Access your personal information
- Request correction or deletion of your data
- Withdraw consent where applicable
- Opt out of non-essential communications

Requests can be made by contacting us using the details below.

---

9. CHILDREN’S PRIVACY
Vagmee is not intended for individuals under the age of 18. We do not knowingly collect personal data from minors.

---

10. CHANGES TO THIS POLICY
We may update this Privacy Policy from time to time. Updates will be posted on this page with a revised “Last Updated” date. Continued use of the Service constitutes acceptance of the updated policy.

---

11. CONTACT US
If you have questions or concerns regarding this Privacy Policy, please contact us at:

Email: support@vagmee.in
`
},

    refund: {
  title: "Refund Policy",
  content: `
REFUND POLICY

Last Updated: January 2026

Thank you for choosing Vagmee. This Refund Policy explains the circumstances under which refunds may be issued for purchases made on our platform.

---

1. GENERAL POLICY
Vagmee provides digital services related to interview practice, analysis, and reporting. Due to the nature of digital services, refunds are limited and provided only under specific circumstances outlined below.

By making a payment on Vagmee, you acknowledge and agree to this Refund Policy.

---

2. REFUND ELIGIBILITY & TIME LIMIT
Refund requests must be submitted **within 3 days (72 hours)** of the original transaction date.

Any refund request raised **after 3 days** of the transaction will **not be considered**, regardless of usage status.

---

3. ELIGIBLE REFUND CONDITIONS
Refund requests may be considered only in the following cases:

- A verified technical issue on our platform prevented you from starting a purchased interview session
- Duplicate payment or accidental multiple charges for the same service
- Unauthorized transaction reported within the eligible refund window
- Payment was deducted but the interview session was not initiated and no report was generated

Refunds are **not guaranteed** and are subject to review and approval.

---

4. NON-REFUNDABLE CASES
Refunds will NOT be issued for:

- Interview sessions that have been started or completed
- AI-generated reports, feedback, or analysis once delivered
- Dissatisfaction with interview performance, scores, or outcomes
- Change of mind after purchase
- Subscription periods that are partially used (if applicable)
- Delays or issues caused by user-side factors such as internet connectivity, device limitations, or microphone permissions
- Refund requests submitted after 3 days (72 hours) from the transaction date

---

5. REFUND REQUEST PROCESS
To request a refund:

1. Email support@vagmee.in with the subject line: "Refund Request"
2. Include your registered email address, transaction ID, and reason for the request
3. Our team will review the request within 5–7 business days
4. If approved, the refund will be processed to the original payment method

---

6. REFUND TIMELINE
- Review & decision: 5–7 business days
- Refund initiation after approval: Up to 10 business days
- Final credit time depends on your bank or payment provider

---

7. PARTIAL REFUNDS
Partial refunds may be issued at our sole discretion in rare cases where:
- A paid session was interrupted due to verified platform issues
- Only part of the purchased service could be delivered

The decision on partial refunds rests solely with Vagmee.

---

8. SERVICE AVAILABILITY & BETA DISCLAIMER
Vagmee may offer early access or beta features. Users acknowledge that beta services may experience delays or limitations. Refunds for beta usage are provided strictly in accordance with this policy.

---

9. POLICY UPDATES
Vagmee reserves the right to modify this Refund Policy at any time. Updates will be posted on this page with a revised "Last Updated" date. Continued use of the platform constitutes acceptance of the updated policy.

---

10. CONTACT US
For refund-related queries, please contact:

Email: support@vagmee.in
Business Hours: Monday–Friday, 9:00 AM – 6:00 PM IST
`
},
terms: {
  title: "Terms & Conditions",
  content: `
TERMS & CONDITIONS

Last Updated: January 2026

These Terms & Conditions ("Terms") govern your access to and use of the Vagmee platform, website, and services (collectively, the "Service"). By accessing or using Vagmee, you agree to be bound by these Terms.

If you do not agree to these Terms, please do not use the Service.

---

1. ABOUT THE SERVICE
Vagmee is an AI-powered interview practice and assessment platform designed to help users simulate interviews and receive automated feedback. The Service is intended for practice, preparation, and informational purposes only.

Vagmee does NOT guarantee interview success, job offers, hiring outcomes, or career advancement.

---

2. ELIGIBILITY
You must be at least 18 years old to use Vagmee. By using the Service, you confirm that you meet this eligibility requirement.

---

3. ACCOUNT REGISTRATION
- Users may register using third-party authentication methods (such as Google login).
- You are responsible for maintaining the confidentiality of your account credentials.
- You agree to provide accurate and up-to-date information, including a valid email address and mobile number.
- Vagmee is not responsible for unauthorized access caused by user negligence.

---

4. SERVICE NATURE & AI DISCLAIMER
- Vagmee uses artificial intelligence and automated systems to generate interview questions, transcriptions, analysis, scores, and reports.
- AI-generated outputs are based on probabilistic models and may contain inaccuracies or limitations.
- All feedback, scores, and reports are provided for informational and practice purposes only and should not be treated as professional, hiring, legal, or career advice.

---

5. BETA & EARLY ACCESS DISCLAIMER
Vagmee may offer early access or beta features. Users acknowledge that:
- The Service may contain bugs, delays, or limitations
- Features may change or be modified without prior notice
- Service availability is not guaranteed at all times

By using beta features, you accept these risks.

---

6. PAYMENTS & PRICING
- Certain features of Vagmee require payment.
- All prices are displayed before checkout and are subject to change at Vagmee’s discretion.
- Payments must be made through supported payment gateways.
- Successful payment grants access only to the specified service or session.

---

7. REFUNDS & CANCELLATIONS
Refunds are governed strictly by our Refund Policy. By making a payment, you acknowledge that:
- Digital services once started or delivered are non-refundable
- Refund requests must follow the conditions and timelines defined in the Refund Policy

---

8. ACCEPTABLE USE
You agree NOT to:
- Misuse or abuse the Service
- Attempt to reverse engineer or manipulate the platform
- Share login credentials with others
- Upload malicious, illegal, or offensive content
- Use the Service for unlawful or unethical purposes

Vagmee reserves the right to suspend or terminate accounts that violate these Terms.

---

9. INTELLECTUAL PROPERTY
All content, software, models, reports, branding, and materials on Vagmee are the intellectual property of Vagmee or its licensors.

You may not copy, reproduce, distribute, or exploit any part of the Service without prior written permission.

---

10. LIMITATION OF LIABILITY
To the maximum extent permitted by law:
- Vagmee shall not be liable for any indirect, incidental, consequential, or special damages
- Vagmee is not responsible for hiring decisions, interview outcomes, or employment results
- Total liability, if any, shall not exceed the amount paid by you for the specific service in question

---

11. SERVICE AVAILABILITY
Vagmee does not guarantee uninterrupted or error-free operation. We may:
- Modify or discontinue features
- Perform maintenance
- Temporarily suspend access for technical reasons

---

12. TERMINATION
Vagmee reserves the right to suspend or terminate access to the Service at its discretion, including in cases of misuse, violation of these Terms, or suspected fraud.

---

13. GOVERNING LAW & JURISDICTION
These Terms shall be governed by and construed in accordance with the laws of India. Any disputes shall be subject to the exclusive jurisdiction of the courts of India.

---

14. CHANGES TO TERMS
We may update these Terms from time to time. Changes will be posted on this page with a revised "Last Updated" date. Continued use of the Service constitutes acceptance of the updated Terms.

---

15. CONTACT INFORMATION
For questions regarding these Terms, please contact:

Email: support@vagmee.in
`
}


  };

  const viewPolicy = (policyType) => {
    setSelectedPolicy(policyType);
    setShowPolicyModal(true);
  };

  const closeModal = () => {
    setShowPolicyModal(false);
    setSelectedPolicy(null);
  };

  return (
    <div className="aboutus-container">
      {/* Animated background */}
      <div className="background-animation">
        <div className="gradient-blob blob-1"></div>
        <div className="gradient-blob blob-2"></div>
        <div className="gradient-blob blob-3"></div>
      </div>

      {/* Navigation */}
      <nav className="navbar">
        <div className="nav-content">
          <button onClick={() => navigate(-1)} className="back-button">← Back</button>
          <div className="logo-section">
            <div className="logo-icon">
              <img src="/Vagmee_Logo.png" alt="Vagmee" className="nav-logo" />
            </div>
            <div className="logo-branding">
              <span className="logo-text">Vagmee</span>
              <span className="logo-tagline">Practice smarter. Get hired faster.</span>
            </div>
          </div>
          <div style={{ width: "120px" }}></div> {/* Spacer for alignment */}
        </div>
      </nav>

      {/* About Section */}
      <section className="about-hero">
        <div className="about-hero-content">
          <h1 className="about-hero-title">About <span className="gradient-text">Vagmee</span></h1>
          </div>
          <p className=".about-hero-subtitle">
            Empowering job seekers with AI-powered mock interviews and real-time feedback to help them land their dream roles.
          </p>
        
      </section>

      {/* Mission Section */}
      <section className="mission-section" id="mission-section">
        <div className="mission-content glass-card">
          <h2 className="section-title">Our Mission</h2>
          <p className="mission-text">
            At Vagmee, we believe that every candidate deserves a fair chance to showcase their potential. 
            Our mission is to democratize interview preparation by providing intelligent, adaptive mock interviews 
            that help candidates build confidence, improve their communication skills, and ultimately land their 
            dream positions.
          </p>
        </div>
      </section>

      {/* Values Section */}
      <section className="values-section">
        <h2 className="section-title">Our Core Values</h2>
        <div className="values-grid">
          <div className="value-card glass-card">
            <div className="value-icon">🎯</div>
            <h3 className="value-title">Excellence</h3>
            <p className="value-text">We strive for the highest quality in our AI models and user experience.</p>
            <div className="feature-accent"></div>
          </div>

          <div className="value-card glass-card">
            <div className="value-icon">🤝</div>
            <h3 className="value-title">Empathy</h3>
            <p className="value-text">We understand the stress of interviews and provide supportive, constructive feedback.</p>
            <div className="feature-accent"></div>
          </div>

          <div className="value-card glass-card">
            <div className="value-icon">💡</div>
            <h3 className="value-title">Innovation</h3>
            <p className="value-text">We continuously improve our AI algorithms to deliver better results for our users.</p>
            <div className="feature-accent"></div>
          </div>

          <div className="value-card glass-card">
            <div className="value-icon">🔒</div>
            <h3 className="value-title">Trust</h3>
            <p className="value-text">We protect user data and maintain the highest standards of security and privacy.</p>
            <div className="feature-accent"></div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="contact-section" id="contact-section">
        <div className="contact-content glass-card">
          <h2 className="section-title">Get In Touch</h2>
          <p className="section-subtitle">We'd love to hear from you. Reach out to us for any questions or feedback.</p>
          
          <div className="contact-grid">
            <div className="contact-item">
              <div className="contact-icon">📧</div>
              <h3>Email</h3>
              <p>
                <a href="mailto:support@vagmee.in">support@vagmee.in</a>
              </p>
            </div>

            {/* <div className="contact-item">
              <div className="contact-icon">📱</div>
              <h3>Phone</h3>
              <p>
                <a href="tel:+1-800-VAGMEE-1">+1-800-VAGMEE-1</a>
              </p>
            </div> */}

            <div className="contact-item">
              <div className="contact-icon">📍</div>
              <h3>Address</h3>
              <p>E442, SLS Square Apartment , <br />EPIP zone , Whitefield<br />Bengaluru 560066</p>
            </div>

            <div className="contact-item">
              <div className="contact-icon">🕒</div>
              <h3>Business Hours</h3>
              <p>Monday - Friday: 9:00 AM - 6:00 PM<br />EST (UTC-5)</p>
            </div>
          </div>
        </div>
      </section>

      {/* Policies Section */}
      <section className="policies-section" id="policies-section">
        <div className="policies-content">
          <h2 className="section-title">Legal Documents</h2>
          <p className="section-subtitle">Read our policies to understand how we protect your rights and data.</p>
          
          <div className="policies-buttons">
            <button 
              onClick={() => viewPolicy("privacy")} 
              className="policy-button glass-card secondary"
            >
              <span className="button-icon">📄</span>
              View Privacy Policy
            </button>
            <button 
              onClick={() => viewPolicy("refund")} 
              className="policy-button glass-card secondary"
            >
              <span className="button-icon">📄</span>
              View Refund Policy
            </button>
            <button 
              onClick={() => viewPolicy("terms")} 
              className="policy-button glass-card secondary"
            >
              <span className="button-icon">📄</span>
              View Terms & Conditions
            </button>
          </div>
        </div>
      </section>

      {/* Team Section */}
      {/* <section className="team-section">
        <h2 className="section-title">Our Team</h2>
        <div className="team-grid">
          <div className="team-card glass-card">
            <div className="team-avatar">👨‍💼</div>
            <h3 className="team-name">Founder & CEO</h3>
            <p className="team-role">Vision & Strategy</p>
            <p className="team-bio">Leading Vagmee's mission to revolutionize interview preparation.</p>
          </div>

          <div className="team-card glass-card">
            <div className="team-avatar">👩‍💻</div>
            <h3 className="team-name">Head of AI/ML</h3>
            <p className="team-role">Technology & Innovation</p>
            <p className="team-bio">Developing cutting-edge AI models for better interview simulation.</p>
          </div>

          <div className="team-card glass-card">
            <div className="team-avatar">👨‍🏫</div>
            <h3 className="team-name">Head of Content</h3>
            <p className="team-role">Questions & Feedback</p>
            <p className="team-bio">Curating interview questions and providing actionable feedback.</p>
          </div>
        </div>
      </section> */}

      {/* Footer */}
      <footer className="footer">
        <div className="footer-content">
          <div className="footer-section">
            <h4>Vagmee</h4>
            <p>Practice smarter. Get hired faster.</p>
          </div>
          <div className="footer-section">
            <h5>Product</h5>
            <ul>
              <li><button onClick={() => { navigate('/#features'); }} className="footer-link-button">Features</button></li>
              <li><button onClick={() => navigate('/pricing')} className="footer-link-button">Pricing</button></li>
              <li><button onClick={() => { navigate('/#how-it-works'); }} className="footer-link-button">How It Works</button></li>
            </ul>
          </div>
          <div className="footer-section">
            <h5>Company</h5>
            <ul>
              <li><button onClick={() => { const el = document.getElementById('mission-section'); if (el) el.scrollIntoView({ behavior: 'smooth' }); }} className="footer-link-button">About</button></li>
              <li><button onClick={() => { const el = document.getElementById('contact-section'); if (el) el.scrollIntoView({ behavior: 'smooth' }); }} className="footer-link-button">Contact</button></li>
              <li><button onClick={() => { const el = document.getElementById('policies-section'); if (el) el.scrollIntoView({ behavior: 'smooth' }); }} className="footer-link-button">Policies</button></li>
            </ul>
          </div>
        </div>
        <div className="footer-bottom">
          <p>&copy; 2024 Vagmee. All rights reserved.</p>
        </div>
      </footer>

      {/* Policy Modal */}
      {showPolicyModal && selectedPolicy && (
        <div className="policy-modal-overlay">
          <div className="policy-modal">
            <div className="policy-modal-header">
              <h2>{policies[selectedPolicy].title}</h2>
              <button className="policy-modal-close" onClick={closeModal}>✕</button>
            </div>
            <div className="policy-modal-content">
              {policies[selectedPolicy].content.split('\n').map((line, index) => (
                <p key={index}>{line}</p>
              ))}
            </div>
            <div className="policy-modal-footer">
              <button className="policy-modal-button" onClick={closeModal}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

